/**
 * «Destacar» (oct-2026, founder) — elegir una destacada existente o crear una nueva para guardar historias
 * ya publicadas (activas o del archivo). No se vuelve a subir nada: solo se guardan los IDs.
 * Props: open, onOpenChange, storyIds, onDone(highlight)
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Loader2, X, Check, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { highlightsApi } from '@/lib/highlightsApi';
import { friendlyError } from '@/lib/apiError';
import { toast } from 'sonner';
import { tr } from '@/contexts/LanguageContext';

export const HighlightTitleInput = ({ value, onChange, testid = 'highlight-title-input', autoFocus = false }) => (
  <div>
    <input
      value={value}
      onChange={(e) => onChange(e.target.value.slice(0, 15))}
      maxLength={15}
      placeholder={tr("Ej. Fades")}
      autoFocus={autoFocus}
      name="lineage-highlight-title"
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="sentences"
      spellCheck={false}
      inputMode="text"
      data-lpignore="true"
      data-1p-ignore="true"
      className="w-full h-10 rounded-xl border border-border bg-background px-3 text-sm outline-none focus:border-primary"
      data-testid={testid}
    />
    <p className="text-[10px] text-muted-foreground text-right mt-0.5" data-testid={`${testid}-count`}>{value.length}/15</p>
  </div>
);

export const HighlightPickerSheet = ({ open, onOpenChange, storyIds = [], onDone }) => {
  const { user } = useAuth();
  const [list, setList] = useState(null);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!open || !user) return;
    setList(null); setCreating(false); setTitle(''); setBusy(null);
    highlightsApi.list(user.username || user.id).then((d) => setList(d.items || [])).catch(() => setList([]));
  }, [open, user]);

  const addTo = async (h) => {
    setBusy(h.id);
    try {
      const r = await highlightsApi.addStories(h.id, storyIds);
      toast.success(r.added ? tr("Guardada en «{0}»", h.title) : tr("Ya estaba en «{0}»", h.title));
      onDone?.(r.highlight); onOpenChange?.(false);
    } catch (e) { toast.error(friendlyError(e, tr("No pudimos guardar en la destacada"))); } finally { setBusy(null); }
  };
  const create = async () => {
    if (!title.trim()) { toast.error(tr("Ponle un nombre (máx. 15 caracteres)")); return; }
    setBusy('new');
    try {
      const r = await highlightsApi.create({ title: title.trim(), storyIds });
      toast.success(tr("Destacada «{0}» creada", r.highlight.title));
      onDone?.(r.highlight); onOpenChange?.(false);
    } catch (e) { toast.error(friendlyError(e, tr("No pudimos crear la destacada"))); } finally { setBusy(null); }
  };

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[210] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center"
      onClick={() => !busy && onOpenChange?.(false)} onPointerDown={(e) => e.stopPropagation()} onPointerUp={(e) => e.stopPropagation()} data-testid="highlight-picker-sheet">
      <div className="w-full sm:max-w-md max-h-[85vh] rounded-t-3xl sm:rounded-3xl border border-border bg-card text-foreground flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <h3 className="font-display text-base font-bold flex items-center gap-2"><Star className="w-4 h-4 text-primary" />{tr("Guardar en destacada")}</h3>
          <button type="button" onClick={() => onOpenChange?.(false)} className="w-8 h-8 rounded-full hover:bg-secondary flex items-center justify-center" aria-label={tr("Cerrar")} data-testid="highlight-picker-close"><X className="w-4 h-4" /></button>
        </div>
        <p className="px-4 text-[11px] text-muted-foreground">{tr("{0} historia(s). La historia sigue en tu riel 24 h y queda guardada aquí para siempre.", storyIds.length)}</p>
        <div className="flex-1 overflow-y-auto px-3 py-3 space-y-1.5">
          {list === null && <p className="text-xs text-muted-foreground px-1 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" />{tr("Cargando…")}</p>}
          {(list || []).map((h) => {
            const already = storyIds.every((id) => (h.story_ids || []).includes(id));
            return (
              <button key={h.id} type="button" disabled={Boolean(busy)} onClick={() => addTo(h)} data-testid={`highlight-picker-item-${h.id}`}
                className="w-full flex items-center gap-3 rounded-2xl border border-border bg-background px-3 py-2 text-left hover:border-primary/60 disabled:opacity-60">
                <span className="w-11 h-11 rounded-full overflow-hidden border border-primary/40 bg-muted shrink-0">{h.cover_url && <img src={h.cover_url} alt="" className="w-full h-full object-cover" />}</span>
                <span className="flex-1 min-w-0"><span className="block text-sm font-semibold truncate">{h.title}</span><span className="block text-[11px] text-muted-foreground">{tr("{0} historia(s)", h.count ?? (h.story_ids || []).length)}</span></span>
                {busy === h.id ? <Loader2 className="w-4 h-4 animate-spin text-primary" /> : already ? <Check className="w-4 h-4 text-emerald-400" /> : <Plus className="w-4 h-4 text-primary" />}
              </button>
            );
          })}
          {list && !creating && (
            <button type="button" onClick={() => setCreating(true)} className="w-full flex items-center gap-3 rounded-2xl border border-dashed border-primary/50 px-3 py-2.5 text-left text-sm font-semibold text-primary hover:bg-primary/5" data-testid="highlight-picker-new">
              <span className="w-11 h-11 rounded-full border border-dashed border-primary/50 flex items-center justify-center shrink-0"><Plus className="w-4 h-4" /></span>{tr("Nueva destacada")}
            </button>
          )}
          {creating && (
            <div className="rounded-2xl border border-primary/40 bg-background p-3 space-y-2" data-testid="highlight-picker-create">
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{tr("Nombre de la destacada")}</label>
              <HighlightTitleInput value={title} onChange={setTitle} autoFocus testid="highlight-picker-title" />
              <Button className="w-full rounded-full h-10" disabled={busy === 'new' || !title.trim()} onClick={create} data-testid="highlight-picker-create-btn">
                {busy === 'new' ? <Loader2 className="w-4 h-4 animate-spin" /> : tr("Crear y guardar")}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>, document.body);
};

export default HighlightPickerSheet;
